/**
 * Servicio de caché para URLs de imágenes de S3
 * Maneja el almacenamiento local de URLs con expiración de 3 días
 */

const IMAGE_CACHE_KEY = 'darpha_image_cache';
const CACHE_EXPIRY_DAYS = 3;

class ImageCacheService {
  constructor() {
    this.cache = this.loadCache();
    this.pendingRequests = new Map(); // Almacenar promesas en vuelo
  }

  /**
   * Carga el caché desde localStorage
   */
  loadCache() {
    try {
      const cached = localStorage.getItem(IMAGE_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.error('Error cargando caché de imágenes:', error);
      return {};
    }
  }

  /**
   * Guarda el caché en localStorage
   */
  saveCache() {
    try {
      localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      console.error('Error guardando caché de imágenes:', error);
    }
  }

  /**
   * Genera una clave única para la imagen
   */
  generateCacheKey(imagePath) {
    return `img_${imagePath.replace(/[/\\]/g, '_')}`;
  }

  /**
   * Verifica si una URL en caché sigue siendo válida
   */
  isUrlValid(cacheEntry) {
    if (!cacheEntry || !cacheEntry.url || !cacheEntry.timestamp) {
      return false;
    }

    const now = Date.now();
    const expiryTime = cacheEntry.timestamp + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    
    return now < expiryTime;
  }

  /**
   * Obtiene una URL del caché si está disponible y válida
   */
  getFromCache(imagePath) {
    if (!imagePath) return null;

    const cacheKey = this.generateCacheKey(imagePath);
    const cacheEntry = this.cache[cacheKey];

    if (this.isUrlValid(cacheEntry)) {
      // console.log(`✅ URL obtenida del caché: ${imagePath}`);
      return cacheEntry.url;
    }

    // Si la entrada existe pero ha expirado, la eliminamos
    if (cacheEntry) {
      delete this.cache[cacheKey];
      this.saveCache();
      // console.log(`🗑️ URL expirada eliminada del caché: ${imagePath}`);
    }

    return null;
  }

  /**
   * Almacena una URL en el caché
   */
  setInCache(imagePath, url) {
    if (!imagePath || !url) return;

    const cacheKey = this.generateCacheKey(imagePath);
    
    // Verificar si ya existe y es la misma URL para evitar logs duplicados
    const existing = this.cache[cacheKey];
    if (existing && existing.url === url && this.isUrlValid(existing)) {
      // console.log(`♻️ URL ya existe en caché (no actualizada): ${imagePath}`);
      return;
    }
    
    this.cache[cacheKey] = {
      url,
      timestamp: Date.now(),
      imagePath
    };

    this.saveCache();
    // console.log(`💾 URL almacenada en caché: ${imagePath}`);
  }

  /**
   * Verifica si hay una petición en vuelo para esta imagen
   */
  isPendingRequest(imagePath) {
    return this.pendingRequests.has(imagePath);
  }

  /**
   * Almacena una promesa en vuelo
   */
  setPendingRequest(imagePath, promise) {
    // Limpiar cualquier petición pendiente previa
    if (this.pendingRequests.has(imagePath)) {
      // console.log(`🔄 Reemplazando petición pendiente para: ${imagePath}`);
    }
    
    this.pendingRequests.set(imagePath, promise);
    
    // Limpiar la promesa cuando termine (éxito o error)
    promise.finally(() => {
      this.pendingRequests.delete(imagePath);
      // console.log(`✅ Petición finalizada y limpiada para: ${imagePath}`);
    });
    
    return promise;
  }

  /**
   * Obtiene una promesa en vuelo
   */
  getPendingRequest(imagePath) {
    return this.pendingRequests.get(imagePath);
  }

  /**
   * Fuerza la limpieza de una petición pendiente específica
   */
  clearPendingRequest(imagePath) {
    if (this.pendingRequests.has(imagePath)) {
      this.pendingRequests.delete(imagePath);
      // console.log(`🗑️ Petición pendiente limpiada manualmente para: ${imagePath}`);
      return true;
    }
    return false;
  }

  /**
   * Elimina una entrada específica del caché
   */
  removeFromCache(imagePath) {
    if (!imagePath) return;

    const cacheKey = this.generateCacheKey(imagePath);
    
    if (this.cache[cacheKey]) {
      delete this.cache[cacheKey];
      this.saveCache();
      // console.log(`🗑️ URL eliminada del caché: ${imagePath}`);
    }
  }

  /**
   * Limpia URLs expiradas del caché
   */
  cleanExpiredEntries() {
    let cleanedCount = 0;

    Object.keys(this.cache).forEach(key => {
      const entry = this.cache[key];
      if (!this.isUrlValid(entry)) {
        delete this.cache[key];
        cleanedCount++;
      }
    });

    if (cleanedCount > 0) {
      this.saveCache();
      // console.log(`🧹 ${cleanedCount} URLs expiradas eliminadas del caché`);
    }

    return cleanedCount;
  }

  /**
   * Limpia todo el caché
   */
  clearAllCache() {
    this.cache = {};
    this.saveCache();
    // console.log('🗑️ Caché de imágenes completamente limpiado');
  }

  /**
   * Obtiene estadísticas del caché
   */
  getCacheStats() {
    const totalEntries = Object.keys(this.cache).length;
    let validEntries = 0;
    let expiredEntries = 0;

    Object.values(this.cache).forEach(entry => {
      if (this.isUrlValid(entry)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    });

    return {
      total: totalEntries,
      valid: validEntries,
      expired: expiredEntries,
      cacheSize: this.calculateCacheSize()
    };
  }

  /**
   * Calcula el tamaño aproximado del caché en KB
   */
  calculateCacheSize() {
    try {
      const cacheString = JSON.stringify(this.cache);
      return Math.round(cacheString.length / 1024 * 100) / 100; // KB con 2 decimales
    } catch (error) {
      return 0;
    }
  }

  /**
   * Verifica si el caché necesita mantenimiento
   */
  needsMaintenance() {
    const stats = this.getCacheStats();
    return stats.expired > 0 || stats.total > 100; // Más de 100 entradas o hay expiradas
  }

  /**
   * Ejecuta mantenimiento automático del caché
   */
  performMaintenance() {
    const cleaned = this.cleanExpiredEntries();
    const stats = this.getCacheStats();
    
    // Si aún hay muchas entradas después de limpiar expiradas
    if (stats.total > 150) {
      // Eliminar las más antiguas (estrategia LRU simple)
      const entries = Object.entries(this.cache)
        .map(([key, value]) => ({ key, ...value }))
        .sort((a, b) => a.timestamp - b.timestamp);
      
      const toRemove = entries.slice(0, 50); // Remover las 50 más antiguas
      
      toRemove.forEach(entry => {
        delete this.cache[entry.key];
      });
      
      this.saveCache();
      // console.log(`🧹 ${toRemove.length} entradas antiguas eliminadas por mantenimiento`);
    }

    return { cleaned, maintained: true };
  }
}

// Instancia singleton del servicio de caché
const imageCacheService = new ImageCacheService();

// Ejecutar mantenimiento al inicializar si es necesario
if (imageCacheService.needsMaintenance()) {
  imageCacheService.performMaintenance();
}

export default imageCacheService;
